import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUser } from '../auth';

/**
 * useSafeBack – reliable "go back" for any page.
 *
 * 1. If the browser has in-app history → navigate(-1).
 * 2. Otherwise → role-aware dashboard fallback.
 *
 * Usage:
 *   const safeBack = useSafeBack();           // auto-detect role
 *   const safeBack = useSafeBack('/about');    // explicit fallback
 *   <button onClick={safeBack}>← Back</button>
 */
export default function useSafeBack(fallback) {
  const navigate = useNavigate();

  return useCallback(() => {
    // window.history.length > 2 means there is at least one in-app entry
    // (browsers start at 1 or 2 depending on engine)
    if (window.history.length > 2) {
      navigate(-1);
      return;
    }

    // Determine a safe fallback based on role
    if (fallback) {
      navigate(fallback, { replace: true });
      return;
    }

    const user = getUser();
    const role = user.role; // 'hirer' | 'technician' | 'admin'
    const dashboards = {
      hirer: '/hirer/dashboard',
      technician: '/technician/dashboard',
      admin: '/admin/dashboard',
    };
    navigate(dashboards[role] || '/', { replace: true });
  }, [navigate, fallback]);
}
